import { motion } from "framer-motion";
import { Github, ExternalLink, FolderKanban, ArrowUpRight } from "lucide-react";

const projects = [
  {
    title: "MoodMate",
    subtitle: "AI-Powered Journaling Platform",
    image:
      "https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1200&q=80",
    description:
      "AI journaling platform with emotion analysis, AI-generated insights, and podcast-style summaries for personalized mental wellness.",
    tech: ["React", "Django", "Python", "NLP"],
    github: "https://github.com/sujitaregmi777/MoodMate",
    live: "",
    featured: true,
  },
  {
    title: "RoomSiftay",
    subtitle: "Rental Discovery Platform",
    image:
      "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1200&q=80",
    description:
      "A smart rental platform featuring room discovery, owner verification, location search, and streamlined booking.",
    tech: ["Django", "JavaScript", "SQLite"],
    github: "https://github.com/sujitaregmi777/RoomSiftay",
    live: "",
    featured: true,
  },
  {
    title: "Payroll System",
    subtitle: "Desktop Payroll Management",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
    description:
      "Desktop payroll software with employee management, salary computation, and automated payroll reports.",
    tech: ["Java", "Swing"],
    github: "https://github.com/aakritigharti/Payrollsytem",
    live: "",
    featured: false,
  },
  {
    title: "Interior Design",
    subtitle: "Interior Design Platform",
    image:
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=80",
    description:
      "Modern web platform for showcasing interior projects with booking and client interaction features.",
    tech: ["Django", "Python"],
    github: "https://github.com/sujitaregmi777/Interior-Design",
    live: "",
    featured: false,
  },
];

export default function Projects() {
  return (
    <section
      id="projects"
      className="relative overflow-hidden bg-white py-16 dark:bg-[#030712]"
    >
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.06),transparent_35%)] dark:bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.08),transparent_35%)]" />

      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mx-auto mb-10 max-w-2xl text-center"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-500">
            Portfolio
          </p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-gray-900 md:text-4xl dark:text-white">
            Selected Projects
          </h2>
          <p className="mt-3 text-sm leading-6 text-gray-600 md:text-base dark:text-gray-400">
            A compact selection of projects focused on practical solutions and
            clean design.
          </p>
        </motion.div>

        <div className="grid gap-5 md:grid-cols-2">
          {projects.map((project, index) => (
            <motion.article
              key={project.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.06 }}
              whileHover={{ y: -4 }}
              className="group overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:border-emerald-300 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900"
            >
              <div className="relative overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="h-40 w-full object-cover transition duration-500 group-hover:scale-105 md:h-44"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                <div className="absolute left-4 top-4 flex items-center gap-2">
                  <span className="rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold text-gray-900 backdrop-blur">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  {project.featured && (
                    <span className="rounded-full bg-emerald-500/90 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
                      Featured
                    </span>
                  )}
                </div>

                <div className="absolute right-4 top-4 flex gap-2">
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${project.title} GitHub repository`}
                    className="rounded-full bg-white/90 p-2.5 text-gray-900 backdrop-blur transition hover:bg-emerald-500 hover:text-white"
                  >
                    <Github size={16} />
                  </a>

                  {project.live && (
                    <a
                      href={project.live}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`${project.title} live demo`}
                      className="rounded-full bg-white/90 p-2.5 text-gray-900 backdrop-blur transition hover:bg-emerald-500 hover:text-white"
                    >
                      <ExternalLink size={16} />
                    </a>
                  )}
                </div>
              </div>

              <div className="p-5">
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-emerald-100 p-2.5 dark:bg-emerald-900/20">
                    <FolderKanban size={18} className="text-emerald-600" />
                  </div>

                  <div>
                    <p className="text-xs font-medium text-emerald-500">
                      {project.subtitle}
                    </p>
                    <h3 className="mt-1 text-xl font-bold tracking-tight text-gray-900 dark:text-white">
                      {project.title}
                    </h3>
                  </div>
                </div>

                <p className="mt-4 text-sm leading-6 text-gray-600 dark:text-gray-400">
                  {project.description}
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  {project.tech.map((item) => (
                    <span
                      key={item}
                      className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                    >
                      {item}
                    </span>
                  ))}
                </div>

                <div className="mt-5 flex items-center justify-between gap-3 border-t border-gray-100 pt-4 dark:border-gray-800">
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 transition hover:text-emerald-600 dark:text-white"
                  >
                    <Github size={16} />
                    Source
                  </a>

                  {project.live ? (
                    <a
                      href={project.live}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 transition hover:text-emerald-500"
                    >
                      Live
                      <ArrowUpRight size={16} />
                    </a>
                  ) : (
                    <span className="text-xs text-gray-400">
                      Private
                    </span>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <a
            href="https://github.com/sujitaregmi777"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-md shadow-emerald-600/20 transition hover:bg-emerald-500"
          >
            <Github size={18} />
            View All Projects
            <ArrowUpRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
